import React, { useState, useRef, useCallback, useEffect } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";

export interface Option {
  value: string;
  label: string;
  count?: number;
  /** When true, renders a visual divider line above this option */
  separator?: boolean;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

/**
 * Multi-select dropdown with autocomplete search functionality.
 * Allows users to select multiple options via checkboxes with real-time filtering.
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Wybierz...",
  label,
  disabled = false,
  ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track selections via ref for synchronous access between rapid clicks.
  // Without this, the second click may see stale selectedValues prop when
  // state roundtrips through URL params (setSearchParams → transition → re-render).
  // pendingChangeRef prevents stale props from overwriting our optimistic ref value.
  const valuesRef = useRef(selectedValues);
  const pendingChangeRef = useRef(false);
  useEffect(() => {
    if (pendingChangeRef.current) {
      // Only clear pending flag when props have caught up with our optimistic value.
      // Without this check, intermediate re-renders (caused by unstable prop references
      // like `[] || []`) would clear the flag too early, allowing the next re-render
      // to overwrite valuesRef with stale selectedValues — losing the first click's
      // selection during rapid multi-select.
      const propsMatchOptimistic =
        selectedValues.length === valuesRef.current.length &&
        selectedValues.every((v) => valuesRef.current.includes(v));
      if (propsMatchOptimistic) {
        pendingChangeRef.current = false;
      }
    } else {
      valuesRef.current = selectedValues;
    }
  }, [selectedValues]);

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  // Close dropdown on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const renderedSelectedValues = pendingChangeRef.current
    ? valuesRef.current
    : selectedValues;

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (value: string) => {
    const current = valuesRef.current;
    const newValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    valuesRef.current = newValues;
    pendingChangeRef.current = true;
    onChange(newValues);
  };

  /**
   * Select/deselect all currently visible (filtered) options.
   * When search filter is active, only operates on filtered results.
   * Previously selected options outside the current filter remain unchanged.
   */
  const handleSelectAll = () => {
    const current = valuesRef.current;
    const filteredValues = filteredOptions.map((opt) => opt.value);
    const allFilteredSelected = filteredValues.every((v) =>
      current.includes(v)
    );

    let newValues: string[];
    if (allFilteredSelected) {
      // Deselect only filtered options, keep others
      newValues = current.filter((v) => !filteredValues.includes(v));
    } else {
      // Select all filtered options, preserve existing selections
      const combined = [...current, ...filteredValues];
      newValues = Array.from(new Set(combined));
    }
    valuesRef.current = newValues;
    pendingChangeRef.current = true;
    onChange(newValues);
  };

  const handleClear = () => {
    valuesRef.current = [];
    pendingChangeRef.current = true;
    onChange([]);
  };

  const getDisplayText = () => {
    if (renderedSelectedValues.length === 0) {
      return placeholder;
    }
    if (renderedSelectedValues.length <= 2) {
      return renderedSelectedValues
        .map((v) => options.find((opt) => opt.value === v)?.label)
        .filter(Boolean)
        .join(", ");
    }
    const first2 = renderedSelectedValues
      .slice(0, 2)
      .map((v) => options.find((opt) => opt.value === v)?.label)
      .filter(Boolean)
      .join(", ");
    return `${first2} i ${renderedSelectedValues.length - 2} więcej`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={`relative w-full bg-white border border-neutral-300 rounded-md pl-3 pr-10 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-neutral-400"
        }`}
      >
        <span className={renderedSelectedValues.length === 0 ? "text-neutral-500" : "text-neutral-900"}>
          {getDisplayText()}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`h-5 w-5 text-neutral-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-300 rounded-md shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-neutral-200">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Szukaj..."
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 bg-neutral-50">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium text-primary-600 hover:text-primary-800"
            >
              {filteredOptions.length > 0 &&
              filteredOptions.every((opt) => renderedSelectedValues.includes(opt.value))
                ? "Odznacz widoczne"
                : "Zaznacz widoczne"}
            </button>
            {renderedSelectedValues.length > 0 && (
              <button
                type="button"
                onClick={handleClear}
                className="text-xs font-medium text-neutral-600 hover:text-neutral-800"
              >
                Wyczyść
              </button>
            )}
          </div>

          {/* Options list */}
          <div className="max-h-[300px] overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-neutral-500">
                Brak wyników
              </div>
            ) : (
              filteredOptions.map((option) => (
                <React.Fragment key={option.value}>
                  {option.separator && (
                    <div className="border-t border-neutral-200 my-1" />
                  )}
                <label
                  htmlFor={`multiselect-option-${option.value}`}
                  className="flex items-center px-3 py-2 hover:bg-neutral-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    id={`multiselect-option-${option.value}`}
                    aria-label={option.label}
                    checked={renderedSelectedValues.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                    className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <span className="ml-3 text-sm text-neutral-900">
                    {option.label}
                    {option.count !== undefined && (
                      <span className="ml-1 text-neutral-500">({option.count})</span>
                    )}
                  </span>
                </label>
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
